/*
 * Copyright ish group pty ltd. All rights reserved. https://www.ish.com.au
 * No copying or use of this code is allowed without permission in writing from ish.
 */

import { FieldHeading } from '@api';
import { Epic, ofType, StateObservable } from 'redux-observable';
import { catchError, from, mergeMap, Observable } from 'rxjs';
import { SEND_ADOBE_ANALYTICS_EVENT } from 'src/js/actions/AdobeAnalyticActions';
import { IAction } from 'src/js/actions/IshAction';
import { toFormKey } from 'src/js/components/form/FieldFactory';
import { COMPANY_ADD_FORM_NAME, CONTACT_ADD_FORM_NAME } from 'src/js/enrol/containers/contact-add/actions/Actions';
import { Injector } from 'src/js/injector';
import { AdobeAnalyticsActionArgs } from 'src/js/model/checkout/AdobeAnalytics';
import { FilterParams } from 'src/js/model/checkout/request/Params';
import {
  sendCheckoutEvent,
  sendFormAbandon,
  sendFormSubmit,
  sendPageViewEvent,
  sendPurchaseEvent,
  sendQuickSearchEvent,
  sendSearchEvent
} from 'src/js/services/AdobeAnalyticsService';
import { IshState } from '../services/IshState';

const {
  courseClassesApi,
} = Injector;

declare const window: {
  CoursesUrlFormat: any,
} & Window & typeof globalThis;

const checkContactFormsAbandon = (state: IshState, formKey, formName) => {
  const fieldKeys = Object.keys(state.form[formKey]?.fields || {});
  const lastTouchedField = fieldKeys[fieldKeys.length - 1 || 0];
  sendFormAbandon(
    formName,
    formName,
    lastTouchedField && document.querySelector(`label[for="${lastTouchedField}"]`)?.textContent?.replace("*", '')?.trim()
  )
}

export const AdobeAnalyticsEpic: Epic<any, IshState> = (action$: Observable<any>, store: StateObservable<IshState>): any => {
  return action$.pipe(
    ofType(SEND_ADOBE_ANALYTICS_EVENT),
    mergeMap((action: IAction<AdobeAnalyticsActionArgs>) => {
      const state = store.value;

      if (!state.preferences.adobeAnalyticsEnablde) {
        if (action.payload?.event?.href) {
          window.location.href = action.payload?.event?.href;
        }
        return []
      }

      const { type, event } = action.payload

      const params = new URLSearchParams(location.search);

      switch (type) {
        // Products and courses forms processing
        case 'formSubmit': {
          const { form } = event;
          Object.keys(state.checkout.summary.entities).forEach(entity => {
            if (state.checkout.summary.entities[entity] && state.checkout.summary.entities[entity][form]?.fieldHeadings) {
              state.checkout.summary.entities[entity][form]?.fieldHeadings.forEach((h: FieldHeading) => {
                sendFormSubmit(
                  h.name,
                  `${entity[0].toUpperCase()}${entity.slice(1,entity.length-1)} form`
                )
              })
            }
          })
          break;
        }
        case 'formAbandon': {
          const { forms, href } = event;
          forms.forEach(form => {
            Object.keys(state.checkout.summary.entities).forEach(entity => {
              if (state.checkout.summary.entities[entity] && state.checkout.summary.entities[entity][form]?.fieldHeadings) {
                state.checkout.summary.entities[entity][form]?.fieldHeadings.forEach((h: FieldHeading, index) => {

                  const fields = state.form[form]?.fields;
                  const lastVisited = fields && Object.keys(fields)[Object.keys(fields).length - 1];

                  sendFormAbandon(
                    h.name,
                    `${entity[0].toUpperCase()}${entity.slice(1,entity.length-1)} form`,
                    lastVisited && h.fields.find(f => toFormKey(f.key, index) === lastVisited )?.name
                  )
                })
              }
            })
          })
          if (state.form[CONTACT_ADD_FORM_NAME]) {
            checkContactFormsAbandon(state,CONTACT_ADD_FORM_NAME,'Contact add form');
          }
          if (state.form[COMPANY_ADD_FORM_NAME]) {
            checkContactFormsAbandon(state,COMPANY_ADD_FORM_NAME,'Company add form');
          }
          window.location.href = href;
          break;
        }
        case 'start':
        case 'your details – step 1':
        case 'your details – step 2':
        case 'summary complete': {
          const classes = state.cart.courses.result.map(k => state.cart.courses.entities[k]);
          sendCheckoutEvent(type, classes)
          if ( type === 'start' && event) {
            window.location.href = event.href;
          }
          break;
        }
        case 'purchase': {
          const classes = state.cart.courses.result.map(k => state.cart.courses.entities[k]);
          const amount = state.checkout.amount;

          const { response } = event;

          sendPurchaseEvent(
            classes,
            amount,
            response
          )
          break;
        }
        case 'pageView': {
          const courseIdsSet = new Set<string>();
          document.querySelectorAll('[data-course-id]').forEach((node: any) => {
            courseIdsSet.add(node.dataset.courseId)
          })

          const coursesIds = Array.from(courseIdsSet);

          return from(courseClassesApi.getCourses({
            coursesIds
          })).pipe(
            mergeMap(courses => {
              
              const isSearchResultsPage = document.getElementById('courses-list');

              if (isSearchResultsPage) {

                const params = new URLSearchParams(location.search);

                let hasFilters = false;

                for (const [key] of params) {
                  if (FilterParams.includes(key)) {
                    hasFilters = true;
                    break;
                  }
                }

                if (!hasFilters && window.CoursesUrlFormat) {
                  const parsed = window.CoursesUrlFormat.prototype.parse(location.href);
                  hasFilters = Boolean(parsed?.browseTag);
                }

                if (hasFilters && !params.get('clickTerm')) {
                  sendSearchEvent(
                    courses,
                    params,
                    'searchFilter',
                    false,
                    null
                  )
                }

                if ((!hasFilters && params.get('s')) || params.get('clickTerm')) {
                  sendSearchEvent(
                    courses,
                    params,
                    'search',
                    false,
                    null
                  )
                }
              }
              
              return from(sendPageViewEvent(
                courses,
                state.checkout.contacts[state.checkout.payerId],
                state.checkout.payerId
              )).pipe(
                mergeMap(() => [])
              )
            })
          )
        }
        case 'searchResultClick': {
          const { href, title, courseId } = event;

          if (courseId) {
            return from(courseClassesApi.getCourses({
              coursesIds: [courseId]
            })).pipe(
              mergeMap(courses => {
                sendSearchEvent(
                  courses.length ? [courses[0]] : [],
                  params,
                  'searchResultClick',
                  true,
                  title
                )
                window.location.href = href;
                return [];
              })
            )
          } else {
            sendSearchEvent(
              [],
              params,
              'searchResultClick',
              false,
              title
            )
          }
          window.location.href = href;
          break;
        }
        case 'quickSearch': {
          const { coursesIds } = event;

          if (coursesIds.length) {
            return from(courseClassesApi.getCourses({
              coursesIds
            })).pipe(
              mergeMap(courses => {
                sendQuickSearchEvent(
                  courses,
                  params,
                  'quickSearch',
                  null,
                  null
                )
                return [];
              })
            )
          } else {
            sendQuickSearchEvent(
              [],
              params,
              'quickSearch',
              null,
              null
            )
          }
          break;
        }
        case 'quickSearchhResultClick': {
          const { coursesId, href, title } = event;

          const url = new URL(href);

          if (coursesId) {
            return from(courseClassesApi.getCourses({
              coursesIds: [coursesId]
            })).pipe(
              mergeMap(courses => {
                sendQuickSearchEvent(
                  courses.length ? [courses[0]] : [],
                  params,
                  'quickSearchhResultClick',
                  true,
                  title
                )
                window.location.href = url.toString();
                return [];
              })
            )
          } else {
            url.searchParams.set('clickTerm', title);
            sendQuickSearchEvent(
              [],
              params,
              'quickSearchhResultClick',
              false,
              title
            )
          }

          window.location.href = url.toString();
          break;
        }
      }
      return [];
    }),
    catchError((err, action) => {
      console.error(`Unable to collect Adobe Analytics data \n Reason: ${err}`)
      return action.pipe(
        mergeMap(action => {
          if (action.payload?.event?.href) {
            window.location.href = action.payload?.event?.href;
          }
          return []
        })
      )
    })
  );
};
